import React from 'react';
import componentRegistry from '../injector';
import AccountSummary from './AccountSummary';
import ProductCategories from './ProductCategories';
import MyAccountOrdersPanel from './MyAccountOrdersPanel';
import MyAccountInvoiceHistoryPanel from './MyAccountInvoiceHistoryPanel';
import MyAccountMonthlyOrdersChart from './MyAccountMonthlyOrdersChart';
import PricingCsvExportWidget from './PricingCsvExportWidget';

export interface LayoutRoute {
    path: string;
    component: string;
    props?: Record<string, any>;
}

export interface LayoutPage {
    id: string;
    label: string;
    routes: LayoutRoute[];
    package?: string;
    permissions?: PagePermissionDefinition[];
}

export interface PagePermissionDefinition {
    key: string;
    label: string;
    description?: string;
}

export type PagePermissionValues = Record<string, boolean>;
export type PagePermissionsMap = Record<string, PagePermissionValues>;

export interface PageLayoutWidget {
    id: string;
    settings?: Record<string, any>;
}

export interface PageLayout {
    columnLayout?: ColumnLayoutPreset;
    columns: number;
    columnsData: PageLayoutWidget[][];
}

export type ExpressZoneId =
    | 'pre_cart'
    | 'cart_table'
    | 'cart_actions'
    | 'form_left'
    | 'form_right'
    | 'post_form';

export type ExpressWidgetId =
    | 'notices'
    | 'coupon_toggle'
    | 'cart_table'
    | 'cart_actions'
    | 'billing_fields'
    | 'shipping_fields'
    | 'order_notes'
    | 'summary_totals'
    | 'shipping_methods'
    | 'payment_methods'
    | 'place_order_terms';

export interface ExpressAtomicLayout {
    layoutType: 'express_atomic';
    version?: number;
    zones: Record<ExpressZoneId, ExpressWidgetId[]>;
}

export type MyAccountPageLayoutConfig = PageLayout | ExpressAtomicLayout;
export type PageLayouts = Record<string, MyAccountPageLayoutConfig>;
export type ColumnLayoutPreset = 'one' | 'two_equal' | 'two_split_60_40';

export interface WidgetField {
    key: string;
    label: string;
    type: 'text' | 'number' | 'checkbox' | 'textarea';
    min?: number;
    max?: number;
    placeholder?: string;
    helpText?: string;
    rows?: number;
}

export interface LayoutWidgetDefinition {
    id: string;
    label: string;
    description: string;
    requiredPackage?: string;
    requiredFeatureFlag?: string;
    supportsSettings?: boolean;
    settingsFields?: WidgetField[];
    defaultSettings?: Record<string, any>;
    previewTitle: string;
    render: (settings: Record<string, any>, context: LayoutRenderContext) => React.ReactNode;
}

export interface LayoutRenderContext {
    userName?: string;
    routeComponent?: string;
    routeProps?: Record<string, any>;
    featureFlags?: Record<string, any>;
    enabledPackages?: string[];
}

const routeComponentLabelMap: Record<string, string> = {
    MyAccountDashboard: 'Dashboard',
    MyAccountDetails: 'Account Details',
    MyAccountOrderHistory: 'Order History',
    MyAccountDownloads: 'Downloads',
    MyAccountDraftOrders: 'Draft Orders',
    Favourites: 'My Favourites',
    FitmentToolPage: 'Fitment',
    AddressManagement: 'Address Management',
    BrandCollateral: 'Brand Collateral',
};

const getWidgetSettingValue = (settings: Record<string, any> = {}, key: string) => {
    if (Object.prototype.hasOwnProperty.call(settings, key)) {
        return settings[key];
    }

    const legacyKey = String(key || '').toLowerCase();
    if (legacyKey && Object.prototype.hasOwnProperty.call(settings, legacyKey)) {
        return settings[legacyKey];
    }

    return undefined;
};

const routeComponentWidget: LayoutWidgetDefinition = {
    id: 'route_component',
    label: 'Page Default Component',
    description: 'Renders the default component for this page route.',
    previewTitle: 'Page Default Component',
    render: (_settings, context) => {
        // Always prefer the current route component so saved layouts can't
        // become stale when package-provided routes change.
        const componentName = context.routeComponent || '';

        if (!componentName) {
            return (
                <div className="woocommerce-error" role="alert">
                    Missing route component.
                </div>
            );
        }

        const DynamicComponent = componentRegistry[componentName];
        if (!DynamicComponent) {
            return (
                <div className="woocommerce-error" role="alert">
                    Unknown component: {componentName}
                </div>
            );
        }

        return (
            <DynamicComponent
                userName={context.userName || 'User'}
                featureFlags={context.featureFlags || {}}
                enabledPackages={context.enabledPackages || []}
                {...(context.routeProps || {})}
            />
        );
    },
};

export const layoutWidgetDefinitions: LayoutWidgetDefinition[] = [
    {
        id: 'account_summary',
        label: 'Account Summary',
        description: 'Credit and account snapshot panel.',
        requiredPackage: 'account_summary',
        requiredFeatureFlag: 'accountSummaryEnabled',
        supportsSettings: true,
        settingsFields: [
            { key: 'title', label: 'Widget Title', type: 'text' },
            { key: 'showOrdersBalance', label: 'Show Orders Balance', type: 'checkbox' },
            { key: 'showCreditLimit', label: 'Show Credit Limit', type: 'checkbox' },
            { key: 'showRemainingCredit', label: 'Show Remaining Credit', type: 'checkbox' },
            { key: 'showOverdueDays', label: 'Show Overdue Days', type: 'checkbox' },
            { key: 'showProgressBar', label: 'Show Progress Bar', type: 'checkbox' },
            { key: 'highlightOverdue', label: 'Highlight Overdue State', type: 'checkbox' },
        ],
        defaultSettings: {
            title: 'Account Summary',
            showOrdersBalance: true,
            showCreditLimit: true,
            showRemainingCredit: true,
            showOverdueDays: true,
            showProgressBar: false,
            highlightOverdue: true,
        },
        previewTitle: 'Account Summary',
        render: (settings, context) => (
            <AccountSummary
                settings={{
                    title: getWidgetSettingValue(settings, 'title'),
                    showOrdersBalance: getWidgetSettingValue(settings, 'showOrdersBalance'),
                    showCreditLimit: getWidgetSettingValue(settings, 'showCreditLimit'),
                    showRemainingCredit: getWidgetSettingValue(settings, 'showRemainingCredit'),
                    showOverdueDays: getWidgetSettingValue(settings, 'showOverdueDays'),
                    showProgressBar: getWidgetSettingValue(settings, 'showProgressBar'),
                    highlightOverdue: getWidgetSettingValue(settings, 'highlightOverdue'),
                }}
                defaults={context.featureFlags?.accountSummaryConfig?.widgetDefaults}
            />
        ),
    },
    {
        id: 'product_categories',
        label: 'Product Categories',
        description: 'Category quick-links panel.',
        supportsSettings: true,
        settingsFields: [
            { key: 'title', label: 'Widget Title', type: 'text', placeholder: 'Product Categories' },
            {
                key: 'hidden_categories',
                label: 'Hidden Categories',
                type: 'textarea',
                rows: 4,
                placeholder: 'industrial-tools, 123',
                helpText: 'Enter category slugs or IDs separated by commas or new lines.',
            },
            {
                key: 'whitelisted_categories',
                label: 'Whitelisted Categories',
                type: 'textarea',
                rows: 4,
                placeholder: 'fasteners, 456',
                helpText: 'When provided, only these category slugs or IDs are shown. This takes priority over Hidden Categories.',
            },
        ],
        defaultSettings: {
            title: 'Product Categories',
            hidden_categories: '',
            whitelisted_categories: '',
        },
        previewTitle: 'Product Categories',
        render: (settings) => (
            <ProductCategories
                title={typeof getWidgetSettingValue(settings, 'title') === 'string' && String(getWidgetSettingValue(settings, 'title')).trim() !== ''
                    ? String(getWidgetSettingValue(settings, 'title')).trim()
                    : 'Product Categories'}
                hiddenCategories={typeof getWidgetSettingValue(settings, 'hidden_categories') === 'string'
                    ? String(getWidgetSettingValue(settings, 'hidden_categories'))
                    : ''}
                whitelistedCategories={typeof getWidgetSettingValue(settings, 'whitelisted_categories') === 'string'
                    ? String(getWidgetSettingValue(settings, 'whitelisted_categories'))
                    : ''}
            />
        ),
    },
    {
        id: 'recent_orders',
        label: 'Recent Orders Panel',
        description: 'Compact list of recent orders.',
        requiredPackage: 'order_history',
        supportsSettings: true,
        settingsFields: [
            { key: 'title', label: 'Panel Title', type: 'text' },
            { key: 'maxOrders', label: 'Max Orders', type: 'number', min: 1, max: 50 },
            { key: 'showHeader', label: 'Show Panel Title', type: 'checkbox' },
        ],
        defaultSettings: {
            title: 'Recent Orders',
            maxOrders: 5,
            showHeader: true,
        },
        previewTitle: 'Recent Orders',
        render: (settings) => (
            <MyAccountOrdersPanel
                title={typeof getWidgetSettingValue(settings, 'title') === 'string' && String(getWidgetSettingValue(settings, 'title')).trim() !== ''
                    ? String(getWidgetSettingValue(settings, 'title')).trim()
                    : 'Recent Orders'}
                maxOrders={Number.isFinite(Number(getWidgetSettingValue(settings, 'maxOrders')))
                    ? Number(getWidgetSettingValue(settings, 'maxOrders'))
                    : 5}
                showHeader={getWidgetSettingValue(settings, 'showHeader') !== false}
            />
        ),
    },
    {
        id: 'monthly_orders_chart',
        label: 'Monthly Orders Chart',
        description: '12 month order totals chart.',
        requiredPackage: 'order_history',
        previewTitle: 'Monthly Orders',
        render: (_settings, context) => <MyAccountMonthlyOrdersChart userName={context.userName || 'User'} />,
    },
    {
        id: 'recent_invoices',
        label: 'Recent Invoices Panel',
        description: 'Compact list of recent invoices.',
        requiredPackage: 'invoice_statement',
        supportsSettings: true,
        settingsFields: [
            { key: 'title', label: 'Panel Title', type: 'text' },
            { key: 'maxInvoices', label: 'Max Invoices', type: 'number', min: 1, max: 50 },
            { key: 'showHeader', label: 'Show Panel Title', type: 'checkbox' },
        ],
        defaultSettings: {
            title: 'Recent Invoices',
            maxInvoices: 5,
            showHeader: true,
        },
        previewTitle: 'Recent Invoices',
        render: (settings) => (
            <MyAccountInvoiceHistoryPanel
                title={typeof getWidgetSettingValue(settings, 'title') === 'string' && String(getWidgetSettingValue(settings, 'title')).trim() !== ''
                    ? String(getWidgetSettingValue(settings, 'title')).trim()
                    : 'Recent Invoices'}
                maxInvoices={Number.isFinite(Number(getWidgetSettingValue(settings, 'maxInvoices')))
                    ? Number(getWidgetSettingValue(settings, 'maxInvoices'))
                    : 5}
                showHeader={getWidgetSettingValue(settings, 'showHeader') !== false}
            />
        ),
    },
    {
        id: 'pricing_csv_export',
        label: 'Pricing CSV Download',
        description: 'Download the current user pricing as a CSV file.',
        requiredPackage: 'price',
        requiredFeatureFlag: 'pricingCsvExportEnabled',
        supportsSettings: true,
        settingsFields: [
            { key: 'title', label: 'Widget Title', type: 'text', placeholder: 'My Pricing' },
            {
                key: 'description',
                label: 'Widget Text',
                type: 'textarea',
                rows: 3,
                placeholder: 'Download your current pricing as a CSV file.',
            },
            { key: 'buttonLabel', label: 'Button Text', type: 'text', placeholder: 'Download CSV' },
        ],
        defaultSettings: {
            title: 'My Pricing',
            description: 'Download your current pricing as a CSV file.',
            buttonLabel: 'Download CSV',
        },
        previewTitle: 'Pricing CSV Download',
        render: (settings, context) => (
            <PricingCsvExportWidget
                title={typeof getWidgetSettingValue(settings, 'title') === 'string' && String(getWidgetSettingValue(settings, 'title')).trim() !== ''
                    ? String(getWidgetSettingValue(settings, 'title')).trim()
                    : 'My Pricing'}
                description={typeof getWidgetSettingValue(settings, 'description') === 'string' && String(getWidgetSettingValue(settings, 'description')).trim() !== ''
                    ? String(getWidgetSettingValue(settings, 'description')).trim()
                    : 'Download your current pricing as a CSV file.'}
                buttonLabel={typeof getWidgetSettingValue(settings, 'buttonLabel') === 'string' && String(getWidgetSettingValue(settings, 'buttonLabel')).trim() !== ''
                    ? String(getWidgetSettingValue(settings, 'buttonLabel')).trim()
                    : 'Download CSV'}
                downloadUrl={typeof context.featureFlags?.pricingCsvExportUrl === 'string'
                    ? String(context.featureFlags.pricingCsvExportUrl).trim()
                    : ''}
            />
        ),
    },
    routeComponentWidget,
];

export const layoutWidgetDefinitionsById = layoutWidgetDefinitions.reduce<Record<string, LayoutWidgetDefinition>>((acc, widget) => {
    acc[widget.id] = widget;
    return acc;
}, {});

export const expressZoneOrder: ExpressZoneId[] = [
    'pre_cart',
    'cart_table',
    'cart_actions',
    'form_left',
    'form_right',
    'post_form',
];

export const expressZoneLabels: Record<ExpressZoneId, string> = {
    pre_cart: 'Top Section',
    cart_table: 'Cart Table',
    cart_actions: 'Cart Actions',
    form_left: 'Checkout Left',
    form_right: 'Checkout Right',
    post_form: 'Post Checkout',
};

export const expressWidgetLabels: Record<ExpressWidgetId, string> = {
    notices: 'Notices',
    coupon_toggle: 'Coupon Toggle',
    cart_table: 'Cart Table',
    cart_actions: 'Cart Actions',
    billing_fields: 'Billing Fields',
    shipping_fields: 'Shipping Fields',
    order_notes: 'Order Notes',
    summary_totals: 'Summary Totals',
    shipping_methods: 'Shipping Methods',
    payment_methods: 'Payment Methods',
    place_order_terms: 'Place Order + Terms',
};

export const expressWidgetsByZone: Record<ExpressZoneId, ExpressWidgetId[]> = {
    pre_cart: ['notices', 'coupon_toggle'],
    cart_table: ['cart_table'],
    cart_actions: ['cart_actions'],
    form_left: ['billing_fields', 'shipping_fields', 'order_notes'],
    form_right: ['summary_totals', 'shipping_methods', 'payment_methods', 'place_order_terms'],
    post_form: ['notices'],
};

export function isWidgetEnabled(
    widget: PageLayoutWidget | LayoutWidgetDefinition,
    featureFlags: Record<string, any> = {},
    enabledPackages: string[] = []
): boolean {
    const definition = 'label' in widget
        ? widget as LayoutWidgetDefinition
        : layoutWidgetDefinitionsById[(widget as PageLayoutWidget).id];

    if (!definition) {
        return true;
    }

    if (definition.requiredPackage && !enabledPackages.includes(definition.requiredPackage)) {
        return false;
    }

    const requiredFeatureFlag = definition.requiredFeatureFlag;

    if (!requiredFeatureFlag) {
        return true;
    }

    return featureFlags[requiredFeatureFlag] !== false;
}

export function getWidgetDisplayTitle(widget: PageLayoutWidget): string {
    if (widget.id === 'route_component') {
        const componentName = typeof widget.settings?.component === 'string' ? widget.settings.component : '';
        return routeComponentLabelMap[componentName] || componentName || 'Page Default Component';
    }

    const definition = layoutWidgetDefinitionsById[widget.id];
    if (!definition) {
        return widget.id;
    }

    const title = getWidgetSettingValue(widget.settings || {}, 'title');
    if (typeof title === 'string' && title.trim() !== '') {
        return title.trim();
    }

    return definition.previewTitle;
}

export function normalizePageLayout(layout: any, fallback: PageLayout): PageLayout {
    if (!layout || typeof layout !== 'object') {
        return fallback;
    }

    const allowedLayouts: ColumnLayoutPreset[] = ['one', 'two_equal', 'two_split_60_40'];
    const rawLayout = typeof layout.columnLayout === 'string' ? layout.columnLayout : '';
    const columnLayout: ColumnLayoutPreset = allowedLayouts.includes(rawLayout as ColumnLayoutPreset)
        ? rawLayout as ColumnLayoutPreset
        : (
            Number(layout.columns) > 1
                ? 'two_equal'
                : 'one'
        );

    const columns = columnLayout === 'one' ? 1 : 2;

    const columnsData: PageLayoutWidget[][] = [];
    const rawColumns = Array.isArray(layout.columnsData) ? layout.columnsData : [];

    for (let i = 0; i < columns; i += 1) {
        const source = Array.isArray(rawColumns[i]) ? rawColumns[i] : [];
        const widgets: PageLayoutWidget[] = source
            .filter((item: any) => item && typeof item === 'object' && typeof item.id === 'string')
            .map((item: any) => ({
                id: item.id,
                settings: item.settings && typeof item.settings === 'object' ? item.settings : {},
            }));
        columnsData.push(widgets);
    }

    if (columnsData.every((column) => column.length === 0)) {
        return fallback;
    }

    return {
        columnLayout,
        columns,
        columnsData,
    };
}

export function getDefaultExpressAtomicLayout(): ExpressAtomicLayout {
    return {
        layoutType: 'express_atomic',
        version: 1,
        zones: {
            pre_cart: ['notices', 'coupon_toggle'],
            cart_table: ['cart_table'],
            cart_actions: ['cart_actions'],
            form_left: ['billing_fields', 'shipping_fields', 'order_notes'],
            form_right: ['summary_totals', 'shipping_methods', 'payment_methods', 'place_order_terms'],
            post_form: [],
        },
    };
}

export function isExpressAtomicLayout(layout: any): layout is ExpressAtomicLayout {
    if (!layout || typeof layout !== 'object') {
        return false;
    }

    return layout.layoutType === 'express_atomic' && layout.zones && typeof layout.zones === 'object';
}

export function normalizeExpressAtomicLayout(
    layout: any,
    fallback: ExpressAtomicLayout = getDefaultExpressAtomicLayout()
): ExpressAtomicLayout {
    if (!isExpressAtomicLayout(layout)) {
        return fallback;
    }

    const normalizedZones = { ...fallback.zones };

    expressZoneOrder.forEach((zoneId) => {
        const rawWidgets = Array.isArray(layout.zones?.[zoneId]) ? layout.zones[zoneId] : [];
        const allowedWidgets = new Set<ExpressWidgetId>(expressWidgetsByZone[zoneId]);
        const deduped: ExpressWidgetId[] = [];

        rawWidgets.forEach((widgetId: any) => {
            const safeId = String(widgetId || '').trim() as ExpressWidgetId;
            if (!allowedWidgets.has(safeId) || deduped.includes(safeId)) {
                return;
            }
            deduped.push(safeId);
        });

        normalizedZones[zoneId] = deduped;
    });

    return {
        layoutType: 'express_atomic',
        version: 1,
        zones: normalizedZones,
    };
}

export function getExpressWidgetLibraryForZone(zoneId: ExpressZoneId): ExpressWidgetId[] {
    return [...(expressWidgetsByZone[zoneId] || [])];
}

export function getDefaultLayoutForPage(page: LayoutPage): PageLayout {
    if (page.id === 'dashboard') {
        return {
            columnLayout: 'two_equal',
            columns: 2,
            columnsData: [
                [
                    { id: 'account_summary', settings: {} },
                    {
                        id: 'product_categories',
                        settings: {
                            title: 'Product Categories',
                            hidden_categories: '',
                            whitelisted_categories: '',
                        },
                    },
                ],
                [
                    { id: 'recent_orders', settings: { title: 'Recent Orders', maxOrders: 5, showHeader: true } },
                    { id: 'recent_invoices', settings: { title: 'Recent Invoices', maxInvoices: 5, showHeader: true } },
                ],
            ],
        };
    }

    if (page.id === 'orders') {
        const routeComponent = page.routes?.[0]?.component || 'MyAccountOrderHistory';
        return {
            columnLayout: 'one',
            columns: 1,
            columnsData: [
                [
                    { id: 'monthly_orders_chart', settings: {} },
                    { id: 'route_component', settings: { component: routeComponent } },
                ],
            ],
        };
    }

    const routeComponent = page.routes?.[0]?.component || 'MyAccountDashboard';
    return {
        columnLayout: 'one',
        columns: 1,
        columnsData: [
            [
                {
                    id: 'route_component',
                    settings: { component: routeComponent },
                },
            ],
        ],
    };
}

export function getColumnLayoutOptions(): { id: ColumnLayoutPreset; label: string }[] {
    return [
        { id: 'one', label: '1 Column' },
        { id: 'two_equal', label: '2 Columns (50/50)' },
        { id: 'two_split_60_40', label: '2 Columns (60/40)' },
    ];
}

export function getBuilderWidgetLibrary(page: LayoutPage): LayoutWidgetDefinition[] {
    const routeComponent = page.routes?.[0]?.component || '';

    if (page.id === 'dashboard') {
        return layoutWidgetDefinitions.filter((widget) => widget.id !== 'route_component');
    }

    if (page.id === 'orders') {
        return [
            layoutWidgetDefinitionsById.monthly_orders_chart,
            {
                ...routeComponentWidget,
                previewTitle: routeComponentLabelMap[routeComponent] || routeComponent || 'Page Default Component',
            },
        ].filter(Boolean) as LayoutWidgetDefinition[];
    }

    return [
        {
            ...routeComponentWidget,
            previewTitle: routeComponentLabelMap[routeComponent] || routeComponent || 'Page Default Component',
        },
    ];
}
